(function () {
  if (window.__zenithMediaContentSlider) return;
  window.__zenithMediaContentSlider = true;

  class MediaContentSlider {
    constructor(el) {
      this.el = el;
      this.navItems = Array.from(el.querySelectorAll(".media-content__nav-item"));
      this.bullets = Array.from(el.querySelectorAll(".media-content__bullet"));
      this.visuals = Array.from(el.querySelectorAll(".media-content__visual"));
      this.total = this.visuals.length;

      if (this.total <= 1) return;

      this.current = 0;
      this.autoplay = el.dataset.autoplay === "true";
      this.delay = parseInt(el.dataset.delay, 10) || 6000;
      this.raf = null;
      this.startTime = null;
      this.progressRunId = 0;
      this.currentProgress = 0;
      this.pausedProgress = 0;
      this.isMobile = this._checkMobile();
      this.navHoverPaused = false;

      /* Clicking a nav item or bullet navigates to that slide */
      this.navItems.forEach((item, i) => {
        item.addEventListener("click", () => this._goTo(i, true));

        if (this.autoplay) {
          item.addEventListener("mouseenter", this._onNavItemEnter);
          item.addEventListener("mouseleave", this._onNavItemLeave);
        }
      });
      this.bullets.forEach((btn, i) => {
        btn.addEventListener("click", (e) => {
          e.stopPropagation(); /* nav-item click already handles it */
          this._goTo(i, true);
        });
      });

      this.resizeObserver = new ResizeObserver(() => {
        const wasMobile = this.isMobile;
        this.isMobile = this._checkMobile();
        if (wasMobile !== this.isMobile && this.autoplay) {
          const progress = this.currentProgress;
          this._stopProgress();
          this._startProgress(progress);
        }
      });
      this.resizeObserver.observe(this.el);

      if (this.autoplay) this._startProgress();

      this.visuals.forEach((visual, i) => {
        if (i !== this.current) this._pauseSlideMedia(visual);
      });
    }

    destroy() {
      this._stopProgress();
      if (this.resizeObserver) this.resizeObserver.disconnect();
      this.navItems.forEach((item) => {
        item.removeEventListener("mouseenter", this._onNavItemEnter);
        item.removeEventListener("mouseleave", this._onNavItemLeave);
      });
    }

    _onNavItemEnter = () => {
      this.navHoverPaused = true;
      this._stopProgress(false);
    };

    _onNavItemLeave = () => {
      requestAnimationFrame(() => {
        const stillOverNav = this.navItems.some((item) => item.matches(":hover"));
        if (stillOverNav) return;
        this.navHoverPaused = false;
        if (this.autoplay) this._startProgress(this.pausedProgress);
      });
    };

    _pauseSlideMedia(visual) {
      if (!visual) return;

      visual.querySelectorAll("video.media-content__video").forEach((video) => {
        video.pause();
        video.currentTime = 0;
      });

      visual.querySelectorAll("iframe.media-content__video--iframe").forEach((iframe) => {
        this._pauseIframe(iframe);
      });
    }

    _restartSlideMedia(visual) {
      if (!visual) return;

      visual.querySelectorAll("video.media-content__video").forEach((video) => {
        video.currentTime = 0;
        video.play().catch(() => {});
      });

      visual.querySelectorAll("iframe.media-content__video--iframe").forEach((iframe) => {
        this._restartIframe(iframe);
      });
    }

    _pauseIframe(iframe) {
      const src = iframe.src;

      if (src.includes("youtube.com")) {
        iframe.contentWindow?.postMessage(
          '{"event":"command","func":"pauseVideo","args":""}',
          "*"
        );
        return;
      }

      if (src.includes("player.vimeo.com")) {
        iframe.contentWindow?.postMessage('{"method":"pause"}', "*");
      }
    }

    _restartIframe(iframe) {
      const src = iframe.getAttribute("src");
      if (!src) return;
      iframe.setAttribute("src", src);
    }

    /* ── Navigation ── */

    _goTo(index, userInitiated = false) {
      if (index === this.current) return;
      this._stopProgress();

      if (userInitiated) {
        this.navHoverPaused = false;
      }

      const prev = this.current;
      this.current = index;

      /* Only the media panel transitions */
      this.visuals[prev].classList.remove("is-active");
      this.visuals[index].classList.add("is-active");

      this._pauseSlideMedia(this.visuals[prev]);
      this._restartSlideMedia(this.visuals[index]);

      /* Update nav items (opacity) */
      this.navItems[prev].classList.remove("is-active");
      this.navItems[index].classList.add("is-active");

      this._updateBulletStates(index);

      if (this.autoplay) this._startProgress(0);
    }

    _updateBulletStates(activeIndex) {
      this.bullets.forEach((btn, i) => {
        const isActive = i === activeIndex;
        const isDone = i < activeIndex;

        btn.classList.toggle("is-active", isActive);
        btn.classList.toggle("is-done", isDone);
        btn.setAttribute("aria-current", isActive ? "true" : "false");

        const fill = btn.querySelector(".media-content__bullet-fill");
        if (!fill) return;

        if (isDone) {
          fill.style.removeProperty("transform");
          return;
        }

        if (!isActive) {
          fill.style.transform = this._fillTransform(0);
        }
      });

      const activeBullet = this.bullets[activeIndex];
      if (activeBullet) {
        this._setFill(activeBullet, 0);
      }
    }

    _next() {
      this._goTo((this.current + 1) % this.total);
    }

    /* ── Progress animation ── */

    _startProgress(initialProgress = 0) {
      if (!this.autoplay || this.navHoverPaused) return;

      this._cancelProgressAnimation();

      const bullet = this.bullets[this.current];
      if (!bullet) return;

      const delay = this.delay;
      const runId = ++this.progressRunId;
      const startProgress = Math.min(Math.max(initialProgress, 0), 1);

      this.currentProgress = startProgress;
      this._setFill(bullet, startProgress);

      const tick = (timestamp) => {
        if (runId !== this.progressRunId) return;

        if (!this.startTime) {
          this.startTime = timestamp - startProgress * delay;
        }

        const progress = Math.min((timestamp - this.startTime) / delay, 1);
        this.currentProgress = progress;
        this._setFill(bullet, progress);

        if (progress < 1) {
          this.raf = requestAnimationFrame(tick);
        } else {
          this._setFill(bullet, 1);
          this.currentProgress = 1;
          this._next();
        }
      };

      this.raf = requestAnimationFrame(tick);
    }

    _cancelProgressAnimation() {
      if (this.raf) {
        cancelAnimationFrame(this.raf);
        this.raf = null;
      }
      this.startTime = null;
    }

    _stopProgress(reset = true) {
      this.progressRunId += 1;
      this._cancelProgressAnimation();

      if (reset) {
        this.currentProgress = 0;
        this.pausedProgress = 0;
      } else {
        this.pausedProgress = this.currentProgress;
      }
    }

    _fillTransform(progress) {
      return this.isMobile ? `scaleX(${progress})` : `scaleY(${progress})`;
    }

    _setFill(bullet, progress) {
      if (!bullet?.classList.contains("is-active")) return;

      const fill = bullet.querySelector(".media-content__bullet-fill");
      if (!fill) return;

      fill.style.transform = this._fillTransform(progress);
    }

    _checkMobile() {
      return window.matchMedia("(max-width: 989px)").matches;
    }
  }

  function initMediaContentSliders(root = document) {
    const elements =
      root === document
        ? document.querySelectorAll(".media-content")
        : root.querySelectorAll(".media-content");

    elements.forEach((el) => {
      if (!el._mediaContentInstance) {
        el._mediaContentInstance = new MediaContentSlider(el);
      }
    });
  }

  if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", () => initMediaContentSliders());
  } else {
    initMediaContentSliders();
  }

  /* Shopify theme editor: re-init on section reload */
  document.addEventListener("shopify:section:load", (e) => {
    e.target.querySelectorAll(".media-content").forEach((el) => {
      if (el._mediaContentInstance) {
        el._mediaContentInstance.destroy();
        el._mediaContentInstance = null;
      }
      el._mediaContentInstance = new MediaContentSlider(el);
    });
  });
})();
